"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PricingDataError = exports.optionPaintRates = exports.LABOUR_TYPES = exports.DEFAULT_LABOUR_TYPE = void 0;
exports.paintName = paintName;
exports.genericPaints = genericPaints;
exports.labourRateFor = labourRateFor;
exports.toPricingRates = toPricingRates;
exports.hireLine = hireLine;
exports.washIn = washIn;
exports.washAreaIn = washAreaIn;
exports.surfaceInOption = surfaceInOption;
exports.toSections = toSections;
const approved_hours_ts_1 = require("./approved-hours.js");
const measure_ts_1 = require("./measure.js");
const option_scope_ts_1 = require("./option-scope.js");
const pricing_ts_1 = require("./pricing.js");
const surface_rows_ts_1 = require("./surface-rows.js");
const surfaces_ts_1 = require("./surfaces.js");
const job_prices_ts_1 = require("./job-prices.js");
exports.DEFAULT_LABOUR_TYPE = 'Wages';
/** hig_labourtype's labels: the branch's labour-rate rows come one per type. */
exports.LABOUR_TYPES = ['Wages', 'Subbie', 'Mixed'];
const PERCENT = 100;
const NO_RATE = { sell: 0, cost: 0 };
/** The template's three generic paints (Take Off G12:G14), priced from the settings. */
const GENERIC_PAINTS = [
    { key: 'standard', name: 'Standard Paint', setting: 'paint.standard.costperlitre' },
    { key: 'twopacklight', name: 'Two Pack (Light)', setting: 'paint.twopacklight.costperlitre' },
    { key: 'twopackdark', name: 'Two Pack (Dark)', setting: 'paint.twopackdark.costperlitre' },
];
/** A paint's name in an option, from its own list, else the generics'; no key = the option's first paint. For documents, without prices. */
function paintName(scope, key) {
    const paints = (0, option_scope_ts_1.paintsOf)(scope, GENERIC_PAINTS.map(({ key: k, name }) => ({ key: k, name })));
    return (key === undefined ? paints[0] : paints.find((p) => p.key === key))?.name ?? null;
}
/** The generic paints CRM has a price for on the date: where an option's paint list starts. */
function genericPaints(reference) {
    return GENERIC_PAINTS.filter((p) => typeof reference.settings[p.setting] === 'number').map(({ key, name }) => ({ key, name }));
}
/** A paint's book price: its price-book row on the date, else (a generic) its setting; null when it has none. */
function bookCost(paint, reference, products) {
    const product = paint.productId ? products[paint.productId.toLowerCase()] : undefined;
    if (product)
        return product.costPerLitre;
    const generic = GENERIC_PAINTS.find((g) => g.key === paint.key);
    const setting = generic && !paint.productId ? reference.settings[generic.setting] : undefined;
    return typeof setting === 'number' ? setting : null;
}
/**
 * A paint's cost per litre and where it comes from: a one-off paint's job price, else the job's price for
 * the product (every option on the measure, #413), else one typed on this option, else the book on the date.
 */
function paintCost(paint, reference, products, jobPrices) {
    const oneOffId = (0, job_prices_ts_1.oneOffPaintId)(paint.key);
    if (oneOffId) {
        const row = jobPrices.find((p) => p.id === oneOffId);
        if (row?.costPerLitre != null)
            return { costPerLitre: row.costPerLitre, priceSource: 'oneOff' };
        throw new PricingDataError(`${row?.name || paint.name} has no price — type its cost per litre in the job's prices.`);
    }
    const job = paint.productId ? (0, job_prices_ts_1.jobPriceForProduct)(jobPrices, paint.productId) : undefined;
    if (job?.costPerLitre != null)
        return { costPerLitre: job.costPerLitre, priceSource: 'job' };
    if (typeof paint.costPerLitre === 'number')
        return { costPerLitre: paint.costPerLitre, priceSource: 'typed' };
    const book = bookCost(paint, reference, products);
    if (book !== null)
        return { costPerLitre: book, priceSource: 'book' };
    throw new PricingDataError(`${paint.name} has no price on ${reference.date} — check the price book in CRM, or type a job price for it.`);
}
/** A paint as the take-off prices it: its cost per litre, its book price, and its price-book row's coverage when it has one (SCHEMA.md "Which value wins"). */
function paintRate(paint, reference, products, jobPrices) {
    const spread = paint.productId ? products[paint.productId.toLowerCase()]?.spreadRate : null;
    const oneOff = (0, job_prices_ts_1.oneOffPaintId)(paint.key);
    const name = (oneOff && jobPrices.find((p) => p.id === oneOff)?.name) || paint.name;
    return {
        id: paint.key,
        name,
        ...paintCost(paint, reference, products, jobPrices),
        bookCostPerLitre: bookCost(paint, reference, products),
        ...(typeof spread === 'number' && spread > 0 && { spreadRate: spread }),
    };
}
/**
 * The option's paints as pricing rates them, on the reference's date: a price-book paint at its price and spread rate
 * from `products` (resolved on that date). Pricing and the Schedule of Work both use this, so their litres match.
 */
const optionPaintRates = (scope, reference, products = {}, jobPrices = []) => (0, option_scope_ts_1.paintsOf)(scope, genericPaints(reference)).map((p) => paintRate(p, reference, products, jobPrices));
exports.optionPaintRates = optionPaintRates;
/** Settings the price can't be worked out without; a missing one is a CRM data problem, said plainly. */
const REQUIRED_SETTINGS = ['markup.materials', 'markup.other', 'consumables.perlitre', 'wash.rate', 'fuellevy.perhour', 'paint.standard.costperlitre'];
class PricingDataError extends Error {
}
exports.PricingDataError = PricingDataError;
const num = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : 0);
const fraction = (percent) => num(percent) / PERCENT;
const given = (value, fallback) => (typeof value === 'number' ? value : fallback);
/** The branch's rate for a labour type (absent = Wages) on the pricing date; none is a CRM data problem. */
function labourRateFor(reference, labourType) {
    const type = labourType || exports.DEFAULT_LABOUR_TYPE;
    const rate = reference.labourRates.find((r) => r.labourType === type);
    if (!rate?.labour) {
        throw new PricingDataError(`CRM has no ${type} labour rate for ${reference.branch.name ?? 'this branch'} on ${reference.date}.`);
    }
    return rate;
}
/** A sell/cost rate with the option's change, if any, laid over it. */
function changed(base, change) {
    const rate = base ?? NO_RATE;
    return { sell: given(change?.sell, rate.sell), cost: given(change?.cost, rate.cost) };
}
/** One option's rates: the branch's rate and settings on the pricing date, with the option's changes. */
function toPricingRates(reference, option, products = {}, 
/** The measure's job prices (#413): they price every option on the measure. */
jobPrices = []) {
    const missing = REQUIRED_SETTINGS.filter((name) => typeof reference.settings[name] !== 'number');
    if (missing.length)
        throw new PricingDataError(`CRM is missing pricing settings on ${reference.date}: ${missing.join(', ')}.`);
    const s = reference.settings;
    const rate = labourRateFor(reference, option.labourType);
    const own = option.scope.rates ?? {};
    return {
        labour: changed(rate.labour, own.labour),
        overtime: changed(rate.overtime, own.overtime),
        foreman: changed(rate.foreman, own.foreman),
        abseil: changed(rate.abseil, own.abseil),
        allowance: changed(null, own.allowance),
        paints: (0, exports.optionPaintRates)(option.scope, reference, products, jobPrices),
        markupMaterials: fraction(given(option.markupMaterials, s['markup.materials'])),
        markupOther: fraction(given(option.markupOther, s['markup.other'])),
        consumablesPerLitre: given(own.consumables?.sell, s['consumables.perlitre']),
        washRate: given(own.wash?.sell, s['wash.rate']),
        fuelLevyPerHour: given(own.fuelLevy?.sell, s['fuellevy.perhour']),
        negotiatingFactor: fraction(option.negotiatingFactor),
    };
}
const sortOrder = (r) => num(r.fields.hig_sortorder);
const byOrder = (a, b) => sortOrder(a) - sortOrder(b);
/** The hire list Higgins' own rates come from (hig_hireitem.hig_supplier). */
const STANDARD_HIRE_SUPPLIER = 'Standard';
/**
 * Higgins' internal fuel budget, $/week: the Standard hire list's fuel allowance on the pricing date
 * (hig_fuelperweek, the same for every diesel machine on it; the highest if they ever differ). 0 when it has none.
 * A quoted diesel line is fuelled at this rate, never the supplier's (MJ, 2026-10-21, #420).
 */
function dieselFuelPerWeek(hire) {
    return Math.max(0, ...[...hire.values()].filter((h) => h.supplier === STANDARD_HIRE_SUPPLIER).map((h) => h.fuelPerWeek));
}
/**
 * A hire line at its rates: a quoted item from the job's prices, or a hire-list item at the job's price for it,
 * else a rate typed on the line, else the list's on the pricing date (#413). Null when it has none.
 */
function hireLine(line, hire, jobPrices = []) {
    const quoted = line.jobPriceId ? jobPrices.find((p) => p.id === line.jobPriceId) : undefined;
    if (line.jobPriceId && !quoted)
        return null;
    const item = line.hireItemId ? hire.get(line.hireItemId) : undefined;
    const job = quoted ?? (line.hireItemId ? (0, job_prices_ts_1.jobPriceForHire)(jobPrices, line.hireItemId) : undefined);
    if (line.hireItemId && !item && !job && line.dayRate === undefined)
        return null;
    const diesel = quoted ? quoted.diesel : line.diesel;
    const fuel = item ? item.fuelPerWeek : diesel ? dieselFuelPerWeek(hire) : 0;
    return {
        item: quoted?.name || item?.name || line.item || 'Hire',
        dayRate: job?.dayRate ?? given(line.dayRate, item?.dayRate ?? 0),
        transport: job?.transport ?? given(line.transport, item?.transport ?? 0),
        fuelPerWeek: given(line.fuelPerWeek, fuel),
        days: line.days,
        trips: line.trips,
        ...(job && { jobPrice: true }),
    };
}
/** An other-trade line at its rate: a material from the job's prices (#413), else as typed on the line. */
function otherTradeLine(line, jobPrices) {
    const job = line.jobPriceId ? jobPrices.find((p) => p.id === line.jobPriceId) : undefined;
    if (!job)
        return line;
    return { ...line, description: job.name || line.description, rate: job.rate ?? line.rate };
}
/** The area's extra rows for this option (the template's rows 63–84). */
function extras(choice, hire, jobPrices) {
    const access = (choice?.access ?? []).map((line) => hireLine(line, hire, jobPrices));
    return {
        rows: {
            sundries: choice?.sundries,
            washArea: choice?.washArea,
            washRate: choice?.washRate,
            consumablesRate: choice?.consumablesRate,
            scaffoldMoves: choice?.scaffoldMoves,
            access: access.filter((line) => line !== null),
            otherTrades: choice?.otherTrades?.map((line) => otherTradeLine(line, jobPrices)),
        },
        unpriced: (choice?.access ?? []).filter((_, i) => access[i] === null).map((line) => line.item ?? 'A hire item'),
    };
}
/** The side wash is priced on (#510): an internal area's wash row was only ever a spare external one (MJ). */
const WASH_SIDE = 'External';
/** The area's wash in an option (or on site, with no choice): none on an internal area, whatever the site or the option says. */
function washIn(area, doc, choice) {
    return area.fields.hig_side === WASH_SIDE ? (0, option_scope_ts_1.areaWash)(doc, choice) : { wash: false, washOnly: false };
}
/**
 * The wash m² an area is priced with in an option (#510): on an external area, only while Wash is on — the typed m²,
 * else `total` (its kept surfaces'); Wash off prices none, even with an m² left in the scope. An internal area keeps a
 * wash typed at the desk before wash was external only. Undefined = no wash.
 */
function washAreaIn(area, doc, choice, total) {
    if (area.fields.hig_side !== WASH_SIDE)
        return choice?.washArea;
    return washIn(area, doc, choice).wash ? (choice?.washArea ?? total) : undefined;
}
/**
 * A surface as an option prices it: its rows (its own at the option's coats, rate and prep, then each extra paint system,
 * #412) and their hours and litres. Paints change only litres (their spread). Pricing and the Schedule of Work both use
 * this, so the Schedule's figures are the price's.
 */
function surfaceInOption(doc, surface, type, choice, paints = []) {
    const rows = (0, surface_rows_ts_1.surfaceRows)(surface, type, choice, (0, surfaces_ts_1.surfaceCount)(doc, surface), paints);
    const work = rows.map(pricing_ts_1.surfaceWork);
    return { rows, hours: work.reduce((sum, w) => sum + w.hours, 0), litres: work.reduce((sum, w) => sum + w.litres, 0) };
}
/**
 * The option's rows for the surfaces it keeps, and each surface's own hours there (surfaceInOption), as Measured
 * prices them: what Assessed adds for a surface the PM didn't assess (#607).
 */
function optionHours(doc, kept, choice, ctx) {
    const priced = new Map(kept.map((s) => [s.id, surfaceInOption(doc, s, ctx.types.get(s.typeId), choice?.surfaces?.[s.id], ctx.paints)]));
    return { rows: [...priced.values()].flatMap((p) => p.rows), hours: { kept, own: (surface) => priced.get(surface.id)?.hours ?? 0 } };
}
/**
 * One area as a section. Washed: the typed wash m², else the kept surfaces' total (#510). Wash only: the surfaces
 * stay as rows, priced at nothing, and the approved hours (REVIEW's painting hours) aren't used.
 */
function toSection(area, building, doc, surfaces, washArea, ctx) {
    const { scope, hire } = ctx;
    const choice = scope.areas?.[area.id];
    const { washOnly } = washIn(area, doc, choice);
    const { rows, hours } = optionHours(doc, surfaces, choice, ctx);
    const approved = (0, approved_hours_ts_1.pricedApprovedHours)(area.fields, doc, hours.kept, hours.own);
    return {
        id: area.id,
        name: String(area.fields.hig_name ?? ''),
        buildingCount: Math.max(1, num(building.fields.hig_count) || 1),
        side: area.fields.hig_side === WASH_SIDE ? WASH_SIDE : area.fields.hig_side === 'Internal' ? 'Internal' : null,
        approvedHours: typeof approved === 'number' && !washOnly ? approved : null,
        downtime: fraction(area.fields.hig_downtimepercent),
        abseil: fraction(area.fields.hig_abseilpercent),
        overtime: fraction(choice?.overtime),
        foreman: fraction(choice?.foreman),
        surfaces: washOnly ? rows.map((row) => ({ ...row, unpriced: true })) : rows,
        ...(washOnly && { washOnly }),
        ...extras(choice, hire, ctx.jobPrices).rows,
        washArea: washAreaIn(area, doc, choice, washArea),
        surfacesWashArea: washArea,
    };
}
/**
 * The measure's areas, building by building, as pricing sections for one option (+ what the table shows about each).
 * `paints`: the option's priced paints (toPricingRates), so a price-book paint's coverage is the surface's spread.
 */
function toSections(records, measureId, types, scope, hireItems = [], paints = []) {
    const hire = new Map(hireItems.map((h) => [h.id, h]));
    const ctx = { types, scope, hire, paints, jobPrices: (0, job_prices_ts_1.jobPricesOf)(records, measureId) };
    const buildings = records.filter((r) => r.table === 'building' && r.parentId === measureId).sort(byOrder);
    const sections = [];
    const info = new Map();
    for (const building of buildings) {
        const buildingOut = scope.buildings?.[building.id]?.excluded === true;
        const areas = records.filter((r) => r.table === 'area' && r.parentId === building.id).sort(byOrder);
        for (const area of areas) {
            const choice = scope.areas?.[area.id];
            const facts = {
                name: String(area.fields.hig_name ?? ''),
                side: area.fields.hig_side ?? null,
                building: String(building.fields.hig_name ?? ''),
                count: Math.max(1, num(building.fields.hig_count) || 1),
                // Assessed or Other with its figure: priced from approved hours rather than the surfaces' own.
                approved: (0, approved_hours_ts_1.approvedSourceOf)(area.fields) !== 'Measured' && typeof area.fields.hig_approvedhours === 'number',
                excluded: buildingOut || choice?.excluded === true,
                excludedSurfaces: [],
                measured: {},
                paintFlags: {},
                unpricedHire: extras(choice, hire, ctx.jobPrices).unpriced,
                wash: false,
                washOnly: false,
                siteWashArea: 0,
                noGirthSurfaces: [],
                internalWash: area.fields.hig_side !== WASH_SIDE && choice?.washArea !== undefined,
            };
            try {
                const doc = (0, surfaces_ts_1.parseSurfaces)(area.fields.hig_surfaces);
                const all = doc.surfaces;
                const out = all.filter((s) => choice?.surfaces?.[s.id]?.excluded === true);
                facts.excludedSurfaces = out.map((s) => ({ id: s.id, name: (0, surfaces_ts_1.surfaceLabel)(s) }));
                facts.measured = Object.fromEntries(all.flatMap((s) => (0, surface_rows_ts_1.rowsBeforeOption)(s, types.get(s.typeId), choice?.surfaces?.[s.id], paints)));
                facts.paintFlags = Object.fromEntries(all.filter((s) => s.paintFlag).map((s) => [s.id, s.paintFlag?.note ?? '']));
                const outIds = new Set(out.map((s) => s.id));
                facts.hours = (0, approved_hours_ts_1.approvedHoursOf)(area.fields, doc, types, optionHours(doc, all.filter((s) => !outIds.has(s.id)), choice, ctx).hours);
                Object.assign(facts, washIn(area, doc, choice), { siteWashArea: (0, measure_ts_1.areaWashArea)(doc, types, outIds), noGirthSurfaces: (0, measure_ts_1.noGirthSurfaces)(doc, types, outIds) });
                info.set(area.id, facts);
                if (!facts.excluded)
                    sections.push(toSection(area, building, doc, all.filter((s) => !outIds.has(s.id)), facts.siteWashArea, ctx));
            }
            catch (err) {
                info.set(area.id, { ...facts, error: err instanceof Error ? err.message : String(err) });
            }
        }
    }
    return { sections, info };
}
